import type { ReactNode } from 'react';
import { styled } from 'goober';
import { useTranslation } from 'react-i18next';

import { Button } from '@/ui/button/button';
import { Inline } from '@/ui/line/line';

interface ShareLinkCardProps {
  description: string;
  icon: ReactNode;
  isCopyDisabled: boolean;
  onCopy: () => void;
  title: string;
}

export const ShareLinkCard = ({
  description,
  icon,
  isCopyDisabled,
  onCopy,
  title,
}: ShareLinkCardProps) => {
  const { t } = useTranslation('settings');

  return (
    <Card>
      <Inline
        alignItems="center"
        gap="10px"
        justifyContent="start"
        templateColumns="20px auto"
      >
        <IconWrapper>{icon}</IconWrapper>
        <CardTitle>{title}</CardTitle>
      </Inline>
      <Description>{description}</Description>
      <Button
        contentPadding="lg"
        disabled={isCopyDisabled}
        onClick={onCopy}
        size="big"
        startIcon="Copy"
        variant="white"
      >
        <CopyLinkLabel>{t`generalTab.copyInviteLink`}</CopyLinkLabel>
      </Button>
    </Card>
  );
};

const Card = styled('div')`
  background-color: ${({ theme }) => theme.colors.blue.c9};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-width: 401px;
  padding: 24px;
`;

const IconWrapper = styled('span')`
  color: ${({ theme }) => theme.colors.settings.general.shareLinkIcon};
  display: flex;
  flex-shrink: 0;
  height: 20px;
  width: 20px;
`;

const CardTitle = styled('h3')`
  color: ${({ theme }) => theme.colors.basics.black};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  margin: 0;
`;

const CopyLinkLabel = styled('span')`
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 1px;
`;

const Description = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 14px;
  line-height: 20px;
  margin: 9px 0 12px;
  padding-bottom: 12px;
`;
